import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lock, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type {
  UpdateMessagingSettingsPayload,
  MessagingSettings as SettingsValue,
} from '@gokrew/models/messaging';
import { Button } from '@/components/Button';
import { Switch } from '@/components/Switch';
import { useMessaging } from '@/messaging/useMessaging';
import styles from './MessagingSettings.module.css';

const GROUPS: {
  title: string;
  items: (keyof UpdateMessagingSettingsPayload)[];
}[] = [
  {
    title: 'directions',
    items: ['employeeToHr', 'employeeToManager', 'managerToEmployee'],
  },
  { title: 'wider', items: ['sameManagerTeam', 'anyoneInCompany'] },
  { title: 'privacy', items: ['readReceipts'] },
];

export const MessagingSettings = ({
  readOnly: readOnlyProp = false,
}: {
  readOnly?: boolean;
}) => {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const {
    api,
    scope,
    bootstrap,
    settings,
    isLoading,
    isError,
    refresh,
    applySettings,
  } = useMessaging();
  const readOnly = readOnlyProp || bootstrap?.canManageSettings === false;
  const [draft, setDraft] = useState<{
    scope: string;
    baseline: SettingsValue;
    values: UpdateMessagingSettingsPayload;
  } | null>(null);
  const [saved, setSaved] = useState(false);
  const pick = (value: SettingsValue): UpdateMessagingSettingsPayload => ({
    employeeToHr: value.employeeToHr,
    employeeToManager: value.employeeToManager,
    managerToEmployee: value.managerToEmployee,
    sameManagerTeam: value.sameManagerTeam,
    anyoneInCompany: value.anyoneInCompany,
    readReceipts: value.readReceipts,
  });
  useEffect(() => {
    if (!settings) return;
    setDraft((previous) => {
      if (
        !previous ||
        previous.scope !== scope ||
        JSON.stringify(previous.values) ===
          JSON.stringify(pick(previous.baseline))
      )
        return { scope, baseline: settings, values: pick(settings) };
      return previous;
    });
  }, [settings, scope]);
  const values = draft?.scope === scope ? draft.values : null;
  const dirty =
    !!draft &&
    JSON.stringify(draft.values) !== JSON.stringify(pick(draft.baseline));
  const changedElsewhere =
    dirty && draft?.baseline.version !== settings?.version;
  const update = useMutation({
    mutationFn: (payload: UpdateMessagingSettingsPayload) =>
      api.updateSettings(payload),
    onSuccess: (result) => {
      applySettings(result);
      setDraft({ scope, baseline: result, values: pick(result) });
      setSaved(true);
    },
  });
  const s = (key: string) => t(`messaging.settings.${key}`);
  if (isLoading || (!values && settings))
    return (
      <div className={styles.state} role="status">
        {t('messaging.loading')}
      </div>
    );
  if (isError || !values)
    return (
      <div className={styles.state} role="alert">
        <p>{s('load_error')}</p>
        <Button
          onClick={() => {
            void refresh();
          }}
        >
          {t('messaging.retry')}
        </Button>
      </div>
    );
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>{s('title')}</h1>
          <p>{s('subtitle')}</p>
        </div>
        <Button
          disabled={readOnly || update.isPending || !dirty || changedElsewhere}
          onClick={() => {
            setSaved(false);
            update.mutate(values);
          }}
        >
          {s(update.isPending ? 'saving' : 'save')}
        </Button>
      </header>
      <div className={styles.notice}>
        <Info aria-hidden />
        <span>{s('rules_note')}</span>
      </div>
      {changedElsewhere && (
        <div role="alert" className={styles.notice}>
          <span>{s('changed_elsewhere')}</span>
          <Button
            variant="outline"
            onClick={() => {
              if (settings)
                setDraft({ scope, baseline: settings, values: pick(settings) });
              setSaved(false);
            }}
          >
            {s('reload')}
          </Button>
        </div>
      )}
      {dirty && !changedElsewhere && (
        <Button
          variant="outline"
          onClick={() => {
            if (settings)
              setDraft({ scope, baseline: settings, values: pick(settings) });
            setSaved(false);
          }}
        >
          {s('discard')}
        </Button>
      )}
      {readOnly && <p className={styles.help}>{s('read_only')}</p>}
      {GROUPS.map((group) => (
        <section
          className={styles.card}
          key={group.title}
          aria-labelledby={`messaging-${group.title}`}
        >
          <h2 id={`messaging-${group.title}`}>{s(group.title)}</h2>
          {group.title === 'directions' && (
            <div className={styles.row}>
              <div className={styles.text}>
                <span className={styles.label}>{s('hr_always')}</span>
                <p className={styles.help}>{s('hr_always_help')}</p>
              </div>
              <Lock size={18} aria-label={s('hr_always')} />
            </div>
          )}
          {group.items.map((key) => (
            <div className={styles.row} key={key}>
              <div className={styles.text}>
                <label htmlFor={`messaging-${key}`} className={styles.label}>
                  {s(key)}
                </label>
                <p id={`messaging-${key}-help`} className={styles.help}>
                  {s(`${key}_help`)}
                </p>
              </div>
              <Switch
                id={`messaging-${key}`}
                aria-describedby={`messaging-${key}-help`}
                checked={values[key]}
                disabled={readOnly || update.isPending}
                onCheckedChange={(checked) => {
                  setSaved(false);
                  setDraft((current) =>
                    current
                      ? {
                          ...current,
                          values: { ...current.values, [key]: checked },
                        }
                      : current
                  );
                }}
              />
            </div>
          ))}
        </section>
      ))}
      {update.isError && (
        <p role="alert" className={styles.error}>
          {s('save_error')}
        </p>
      )}
      {saved && <p role="status">{s('saved')}</p>}
    </div>
  );
};
